/**
 * PURPOSE:
 * Dibujar las conexiones SVG entre los partidos de la llave pública del evento 17.
 *
 * RESPONSIBILITIES:
 * - Medir cards y slots después del render.
 * - Trazar uniones reales desde el partido de origen hasta el slot de destino.
 * - Diferenciar sutilmente las conexiones de perdedores hacia el tercer puesto.
 *
 * COLLABORATORS:
 * - Event17PublicBracket.jsx
 * - Event17PublicBracketMatch.jsx
 *
 * ROUTE:
 * src/components/Fixture/components/Event17PublicBracketConnectors.jsx
 */
import React, { useCallback, useEffect, useState } from "react";

const escapeAttribute = (value) => String(value || "").replace(/"/g, '\\"');

export default function Event17PublicBracketConnectors({
  containerRef,
  rounds,
}) {
  const [drawing, setDrawing] = useState({
    width: 0,
    height: 0,
    paths: [],
  });

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const paths = [];

    (rounds || []).forEach((round) => {
      (round.matches || []).forEach((match) => {
        (match.slots || []).forEach((slot) => {
          if (!slot?.sourceMatchCode) return;

          const source = container.querySelector(
            `[data-match-code="${escapeAttribute(slot.sourceMatchCode)}"]`
          );
          const target = container.querySelector(
            `[data-match-code="${escapeAttribute(
              match.code
            )}"] [data-slot-side="${slot.side}"]`
          );

          if (!source || !target) return;

          const sourceRect = source.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const x1 = sourceRect.right - containerRect.left;
          const y1 = sourceRect.top - containerRect.top + sourceRect.height / 2;
          const x2 = targetRect.left - containerRect.left;
          const y2 = targetRect.top - containerRect.top + targetRect.height / 2;
          const middleX = x1 + Math.max(24, (x2 - x1) / 2);

          paths.push({
            key: `${slot.sourceMatchCode}-${match.code}-${slot.side}`,
            d: `M ${x1} ${y1} H ${middleX} V ${y2} H ${x2}`,
            dashed: slot.sourceType === "LOSER",
          });
        });
      });
    });

    setDrawing({
      width: Math.max(container.scrollWidth, container.clientWidth),
      height: Math.max(container.scrollHeight, container.clientHeight),
      paths,
    });
  }, [containerRef, rounds]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const animationFrame = window.requestAnimationFrame(recalculate);
    const delayedCalculation = window.setTimeout(recalculate, 150);
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(recalculate)
        : null;

    observer?.observe(container);
    container
      .querySelectorAll("[data-match-code], [data-slot-side]")
      .forEach((element) => observer?.observe(element));

    window.addEventListener("resize", recalculate);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(delayedCalculation);
      observer?.disconnect();
      window.removeEventListener("resize", recalculate);
    };
  }, [containerRef, recalculate]);

  return (
    <svg
      className="pointer-events-none absolute inset-0 z-0 overflow-visible"
      width={drawing.width}
      height={drawing.height}
      viewBox={`0 0 ${drawing.width} ${drawing.height}`}
      aria-hidden="true"
    >
      {drawing.paths.map((path) => (
        <path
          key={path.key}
          d={path.d}
          fill="none"
          stroke={path.dashed ? "#A78BFA" : "#94A3B8"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={path.dashed ? "5 5" : undefined}
        />
      ))}
    </svg>
  );
}
